import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { NEUTRAL } from '../theme';
import { alertsInBucket, buildAlertSeries } from './alertSpanSeries';

const ROW_H = 8;
const LANE_PAD = 10;

const laneHeight = (lane) => Math.max(44, LANE_PAD * 2 + lane.rows * ROW_H);

const formatTick = (ms, spanMs) => {
  const date = new Date(ms);
  if (spanMs <= 48 * 60 * 60 * 1000) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatHoverTime = (ms) => new Date(ms).toLocaleString('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const axisTicks = (start, end) => {
  const span = end - start;
  const rough = span / 4;
  const steps = [
    60 * 1000,
    5 * 60 * 1000,
    15 * 60 * 1000,
    30 * 60 * 1000,
    60 * 60 * 1000,
    3 * 60 * 60 * 1000,
    6 * 60 * 60 * 1000,
    12 * 60 * 60 * 1000,
    24 * 60 * 60 * 1000,
    7 * 24 * 60 * 60 * 1000,
  ];
  const step = steps.find((item) => item >= rough) || rough;
  const first = Math.ceil(start / step) * step;
  const ticks = [];
  for (let t = first; t < end; t += step) ticks.push(t);
  if (ticks.length === 0) ticks.push(start);
  return ticks;
};

const alertLabel = (alert) => {
  const summary = String(alert.summary || '').trim();
  if (summary) return summary;
  const name = String(alert.policyName || '').split('/').pop();
  return name || alert.component || 'Alert';
};

const AlertSpanChart = ({ alerts, start, end, truncated, onSelect }) => {
  const startMs = start instanceof Date ? start.getTime() : NaN;
  const endMs = end instanceof Date ? end.getTime() : NaN;
  const series = useMemo(
    () => buildAlertSeries(alerts, startMs, endMs),
    [alerts, startMs, endMs]
  );
  const [hover, setHover] = useState(null);

  if (!(endMs > startMs) || series.lanes.length === 0) return null;

  const ticks = axisTicks(startMs, endMs);
  const spanMs = endMs - startMs;
  const hoverTime = hover
    ? startMs + (hover.index + 0.5) * series.bucketMs
    : null;
  const hoverAlerts = hover
    ? series.lanes.flatMap((lane) => alertsInBucket(lane.spans, startMs, series.bucketMs, hover.index).map((span) => ({
      lane,
      alert: span.alert,
      open: span.open,
    })))
    : [];

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(0.9999, Math.max(0, (event.clientX - rect.left) / rect.width));
    setHover({
      index: Math.floor(ratio * series.bucketCount),
      ratio,
    });
  };

  return (
    <Paper
      sx={{
        mb: 2,
        px: 2,
        pt: 1.75,
        pb: 1.25,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
          Alerts over time
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 4, borderRadius: 2, bgcolor: 'text.secondary' }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Open</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 4, borderRadius: 2, bgcolor: 'text.secondary', opacity: 0.35 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Closed</Typography>
          </Box>
          {truncated && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Latest 200</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '148px 1fr', columnGap: 1.5 }}>
        <Box>
          {series.lanes.map((lane) => (
            <Box key={lane.key} sx={{ height: laneHeight(lane), display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: lane.color, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2 }}>{lane.label}</Typography>
                <Typography noWrap sx={{ fontSize: '0.65rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
                  {lane.open} open · {lane.closed} closed
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box>
          <Box
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            onClick={() => {
              if (hoverAlerts.length === 1 && onSelect) onSelect(hoverAlerts[0].alert);
            }}
            sx={{ position: 'relative', cursor: hoverAlerts.length === 1 ? 'pointer' : 'default' }}
          >
            {series.lanes.map((lane) => (
              <Box
                key={lane.key}
                role="img"
                aria-label={`${lane.label}: ${lane.open} open, ${lane.closed} closed`}
                sx={{
                  position: 'relative',
                  height: laneHeight(lane),
                  boxSizing: 'border-box',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {lane.spans.map((span) => {
                  const left = ((span.start - startMs) / spanMs) * 100;
                  const width = Math.max(((span.end - span.start) / spanMs) * 100, 0.4);
                  return (
                    <Box
                      key={span.alert.id || span.alert.incidentId || `${lane.key}-${span.row}-${span.start}`}
                      sx={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${Math.min(width, 100 - left)}%`,
                        top: LANE_PAD + span.row * ROW_H,
                        height: 5,
                        borderRadius: 3,
                        bgcolor: lane.color,
                        opacity: span.open ? 1 : 0.35,
                      }}
                    />
                  );
                })}
              </Box>
            ))}
            {hover && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${(hover.index / series.bucketCount) * 100}%`,
                  width: `${100 / series.bucketCount}%`,
                  bgcolor: NEUTRAL[900],
                  opacity: 0.06,
                  pointerEvents: 'none',
                }}
              />
            )}
            {hover && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: hover.ratio > 0.62 ? `calc(${hover.ratio * 100}% - 8px)` : `calc(${hover.ratio * 100}% + 8px)`,
                  transform: hover.ratio > 0.62 ? 'translateX(-100%)' : 'none',
                  zIndex: 2,
                  pointerEvents: 'none',
                  minWidth: 168,
                  maxWidth: 280,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  boxShadow: 3,
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', mb: 0.5 }}>
                  {formatHoverTime(hoverTime)}
                </Typography>
                {series.lanes.map((lane) => {
                  const open = lane.openCounts[hover.index] || 0;
                  const closed = lane.closedCounts[hover.index] || 0;
                  return (
                    <Box key={lane.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: lane.color }} />
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', flex: 1 }}>{lane.label}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {open} open · {closed} closed
                      </Typography>
                    </Box>
                  );
                })}
                {hoverAlerts.length > 0 && (
                  <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}>
                    {hoverAlerts.slice(0, 4).map(({ lane, alert, open }, index) => (
                      <Typography
                        key={alert.id || alert.incidentId || `${lane.key}-${index}`}
                        noWrap
                        sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.45 }}
                      >
                        {lane.label} · {open ? 'open' : 'closed'} · {alertLabel(alert)}
                      </Typography>
                    ))}
                    {hoverAlerts.length > 4 && (
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                        +{hoverAlerts.length - 4} more
                      </Typography>
                    )}
                    {hoverAlerts.length === 1 && (
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.35 }}>
                        Click to open
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ position: 'relative', height: 22, mt: 0.5 }}>
            {ticks.map((tick) => {
              const ratio = (tick - startMs) / spanMs;
              const shift = ratio < 0.04 ? '0' : ratio > 0.96 ? '-100%' : '-50%';
              return (
                <Typography
                  key={tick}
                  sx={{
                    position: 'absolute',
                    left: `${ratio * 100}%`,
                    transform: `translateX(${shift})`,
                    fontSize: '0.65rem',
                    color: 'text.disabled',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatTick(tick, spanMs)}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default AlertSpanChart;
